import api from 'config/axiosConfig';

class AuthService {
  // In-memory storage for the access token
  private static accessToken: string | null = null;
  // Prevent multiple concurrent refresh requests
  private static refreshPromise: Promise<void> | null = null;
  private static lastRefreshTime: number = 0;
  private static REFRESH_COOLDOWN = 10000; // 10 seconds cooldown between refreshes

  /**
   * Set the access token in memory.
   */
  static setAccessToken(token: string): void {
    this.accessToken = token;
  }

  /**
   * Get the current access token.
   */
  static getAccessToken(): string | null {
    return this.accessToken;
  }

  /**
   * Clear the access token.
   */
  static clearAccessToken(): void {
    this.accessToken = null;
  }

  /**
   * Log in using local authentication.
   * Expects the caller to supply the email and password.
   */
  static async login(email: string, password: string): Promise<void> {
    try {
      const response = await api.post('/auth/login', { email, password });
      if (response.status === 200 && response.data.accessToken) {
        this.setAccessToken(response.data.accessToken);
      } else {
        throw new Error('Login failed: unexpected response');
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Register a new local user.
   * Expects the caller to supply the email, password, username, and PKCE parameters.
   */
  static async register(
    email: string,
    password: string,
    username: string,
    codeVerifier: string,
    codeChallenge: string
  ): Promise<void> {
    try {
      const response = await api.post('/auth/register', {
        email,
        password,
        username,
        code_verifier: codeVerifier,
        code_challenge: codeChallenge,
      });
      if (response.status === 201 && response.data.accessToken) {
        this.setAccessToken(response.data.accessToken);
      } else {
        throw new Error('Registration failed: unexpected response.');
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Refresh the access token.
   * Uses a static refreshPromise to avoid multiple simultaneous refresh calls.
   */
  static async refresh(): Promise<void> {
    // Check if we've refreshed recently
    const now = Date.now();
    if (now - this.lastRefreshTime < this.REFRESH_COOLDOWN) {
      return;
    }

    // If we're already refreshing, wait for that to complete
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    try {
      this.refreshPromise = new Promise(async (resolve, reject) => {
        try {
          const response = await api.post('/auth/refresh');
          if (response.status === 200 && response.data.accessToken) {
            this.setAccessToken(response.data.accessToken);
            this.lastRefreshTime = Date.now();
            resolve();
          } else {
            this.clearAccessToken();
            reject(new Error('Token refresh failed: unexpected response'));
          }
        } catch (error) {
          this.clearAccessToken();
          reject(error);
        }
      });

      await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  /**
   * Log out the current user.
   * Clears the local access token and calls the backend logout endpoint.
   */
  static async logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } finally {
      this.clearAccessToken();
    }
  }
}

export default AuthService;